import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight, FileText, Newspaper, Lightbulb, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type Alert = {
  id: string;
  resource_type: "legislation" | "article" | "idea";
  resource_id: string;
  reason: string | null;
  score: number;
  created_at: string;
  resource_title?: string | null;
  resource_link?: string;
};

const ICONS = {
  legislation: FileText,
  article: Newspaper,
  idea: Lightbulb,
};

const LABELS = {
  legislation: "Bill",
  article: "Insight",
  idea: "Project",
};

async function enrich(alerts: any[]): Promise<Alert[]> {
  const byType: Record<string, string[]> = { legislation: [], article: [], idea: [] };
  alerts.forEach((a) => byType[a.resource_type]?.push(a.resource_id));

  const [bills, articles, ideas] = await Promise.all([
    byType.legislation.length
      ? supabase.from("legislation").select("id, title").in("id", byType.legislation)
      : { data: [] },
    byType.article.length
      ? supabase.from("articles").select("id, title, slug").in("id", byType.article)
      : { data: [] },
    byType.idea.length
      ? supabase.from("ideas").select("id, title").in("id", byType.idea)
      : { data: [] },
  ]);

  const map = new Map<string, { title: string; link: string }>();
  (bills.data ?? []).forEach((b: any) => map.set(`legislation:${b.id}`, { title: b.title, link: `/?bill=${b.id}` }));
  (articles.data ?? []).forEach((a: any) => map.set(`article:${a.id}`, { title: a.title, link: `/insights/${a.slug ?? a.id}` }));
  (ideas.data ?? []).forEach((i: any) => map.set(`idea:${i.id}`, { title: i.title, link: `/ideas/${i.id}` }));

  return alerts.map((a) => {
    const m = map.get(`${a.resource_type}:${a.resource_id}`);
    return { ...a, resource_title: m?.title ?? "Untitled", resource_link: m?.link ?? "/coach" };
  });
}

export default function NewForYouStrip() {
  const { user } = useAuth();
  const qc = useQueryClient();

  // Trigger sweep on mount (per-user, fast)
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: session } = await supabase.auth.getSession();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/coach-relevance-sweep`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.session?.access_token}` },
      }).catch(() => {});
      qc.invalidateQueries({ queryKey: ["coach-alerts", user.id] });
    })();
  }, [user, qc]);

  const { data: alerts, isLoading } = useQuery({
    enabled: !!user,
    queryKey: ["coach-alerts", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("coach_alerts")
        .select("*")
        .eq("user_id", user!.id)
        .is("dismissed_at", null)
        .order("score", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(5);
      return enrich(data ?? []);
    },
  });

  const dismiss = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("coach_alerts")
        .update({ dismissed_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["coach-alerts", user?.id] });
      const prev = qc.getQueryData<Alert[]>(["coach-alerts", user?.id]);
      qc.setQueryData<Alert[]>(["coach-alerts", user?.id], (prev ?? []).filter((a) => a.id !== id));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["coach-alerts", user?.id], ctx.prev);
      toast.error("Could not dismiss");
    },
  });

  // Mark seen on first render
  useEffect(() => {
    if (!alerts?.length) return;
    const unseen = alerts.filter((a: any) => !a.seen_at).map((a) => a.id);
    if (unseen.length) {
      supabase
        .from("coach_alerts")
        .update({ seen_at: new Date().toISOString() })
        .in("id", unseen)
        .then(() => {});
    }
  }, [alerts]);

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking what's new for you…
        </div>
      </div>
    );
  }
  if (!alerts?.length) return null;

  return (
    <section className="bg-card border border-primary/30 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm uppercase tracking-wide">New for you</h3>
        <span className="text-xs text-muted-foreground">· {alerts.length} match{alerts.length === 1 ? "" : "es"}</span>
      </div>
      <ul className="space-y-2">
        {alerts.map((a) => {
          const Icon = ICONS[a.resource_type];
          return (
            <li
              key={a.id}
              className="group flex items-start gap-3 border border-border rounded-lg p-3 hover:border-primary/40 transition-colors"
            >
              <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <Link
                  to={a.resource_link!}
                  className="text-sm font-medium text-foreground hover:text-primary inline-flex items-center gap-1"
                >
                  {a.resource_title}
                  <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                <div className="text-xs text-muted-foreground mt-0.5">
                  <span className="uppercase tracking-wide mr-2 opacity-70">{LABELS[a.resource_type]}</span>
                  {a.reason}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7"
                onClick={() => dismiss.mutate(a.id)}
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}